export interface HotspotSimpleInfo {
  id?: string;
  name?: string;
  description?: string;
  image?: {
    cover?: string;
  };
}

export interface HotspotFeedUser {
  avatar?: string;
  avatarUrl?: string;
  nickName?: string;
  nickname?: string;
  displayName?: string;
}

export interface HotspotFeedAttachment {
  type?: string;
  url?: string;
}

export interface HotspotFeed {
  id: string;
  cover?: string;
  summary?: string;
  creator?: HotspotFeedUser;
  user?: HotspotFeedUser;
  likeCount?: number;
  postShow?: {
    cover?: string;
    summary?: string;
    creator?: HotspotFeedUser;
    user?: HotspotFeedUser;
    likeCount?: number;
    attachments?: HotspotFeedAttachment[];
  };
  attachments?: HotspotFeedAttachment[];
}

export interface HotspotFeedListResponse {
  posts?: HotspotFeed[];
}

export interface HotspotShelfCardTitlePrefix {
  text?: string;
  color?: string;
  link?: string;
}

export interface HotspotShelfCardPrice {
  currency?: string;
  prefix?: string;
  value?: number | string;
  suffix?: string;
  valueV2?: string;
}

export interface HotspotShelfCardDisplay {
  cover?: string;
  title?: string;
  titlePrefix?: HotspotShelfCardTitlePrefix[];
  price?: HotspotShelfCardPrice;
  descriptions?: string[];
}

export interface HotspotShelfItem {
  id?: string;
  type?: string;
  cardDisplay?: HotspotShelfCardDisplay;
}

export interface HotspotShelfSection {
  title?: string;
  count?: number;
  list?: HotspotShelfItem[];
}

export interface HotspotGroupon {
  id?: string;
  title?: string;
  coverImages?: string[];
  eventStartedTime?: string;
  eventEndTime?: string;
}

export interface HotspotGrouponItem {
  groupon?: HotspotGroupon;
  shopInfo?: {
    shopId?: string;
    name?: string;
    avatar?: string;
  };
  seatInfo?: {
    hidenSeatsInfo?: boolean;
  };
  price?: {
    price?: string;
    urgentPrice?: string;
    value?: number;
    suffix?: string;
    Text?: string;
  };
}

export interface HotspotGrouponSection {
  title?: string;
  count?: number;
  list?: HotspotGrouponItem[];
}

export interface HotspotJumpLinkItem {
  title?: string;
  subTitle?: string;
  image?: string;
  jumpUrl?: string;
}

export interface HotspotJumpLinkSection {
  title?: string;
  count?: number;
  list?: HotspotJumpLinkItem[];
}

export interface HotspotSectionItem {
  type?: string;
  shelfInfo?: HotspotShelfSection;
  grouponInfo?: HotspotGrouponSection;
  jumpLinkInfo?: HotspotJumpLinkSection;
}

export interface HotspotSectionResponse {
  hotspotId?: string;
  list?: HotspotSectionItem[];
  filterType?: string;
}

export interface HotspotFeedCard {
  id: string;
  cover?: string;
  summary?: string;
  ratio: number;
  creator?: HotspotFeedUser;
  likeCount?: number;
}

const HOTSPOT_SHELF_TYPE_LABELS: Readonly<Record<string, string>> = {
  BLIND_BOX_MACHINE: "抽盒机",
  B2C: "商城",
  MALL: "商城",
  LUCKY_BAG: "福袋",
  CROWD_FUNDING: "众筹",
  KUJI: "一番赏",
  NEW_LUCKY_BAG: "福袋",
  LOTTERY: "抽选",
  LARP: "剧本杀",
  VIP: "会员卡",
  PURCHASE: "采购",
  UNBOX: "拆盒",
  COMPOSE: "合成",
  LIVE_DRAW: "翻牌机",
  TICKET: "电子票",
  GACHA: "抽卡机",
  LIVE_GROUPON: "直播拼团",
};

export function getHotspotShelfTypeLabel(type?: string): string {
  if (!type) return "";
  return HOTSPOT_SHELF_TYPE_LABELS[type] ?? "";
}

export function getHotspotPriceValue(price?: HotspotShelfCardPrice): string {
  if (!price) return "";
  if (price.valueV2 !== undefined) return String(price.valueV2);
  if (price.value === undefined || price.value === null) return "";
  return String(price.value);
}

export function getHotspotCurrencySymbol(currency?: string): string {
  if (currency === "CNY") return "¥";
  if (currency === "USD") return "$";
  return currency ?? "";
}

export function hasHotspotPriceContent(price?: HotspotShelfCardPrice): boolean {
  return Boolean(
    price && (price.prefix || getHotspotPriceValue(price) || price.suffix)
  );
}

export function formatHotspotGrouponDate(eventStartedTime?: string): string {
  if (!eventStartedTime || Number.isNaN(Number(eventStartedTime))) return "";

  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(new Date(Number(eventStartedTime)));
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  const weekday = parts.find((part) => part.type === "weekday")?.value;

  if (!month || !day || !weekday) return "";

  return `${month}-${day} ${weekday}`;
}

export function selectHotspotGrouponSection(
  response?: HotspotSectionResponse | null
): HotspotGrouponSection | null {
  const section = response?.list?.find((item) => item?.type === "groupon");
  if (!section?.grouponInfo?.list?.length) return null;
  return section.grouponInfo;
}

export function selectHotspotShelfSection(
  response?: HotspotSectionResponse | null
): HotspotShelfSection | null {
  const section = response?.list?.find((item) => item?.type === "shelf");
  if (!section?.shelfInfo?.list?.length) return null;
  return section.shelfInfo;
}

export function selectHotspotJumpLinkSection(
  response?: HotspotSectionResponse | null
): HotspotJumpLinkSection | null {
  const section = response?.list?.find((item) => item?.type === "jump_link");
  if (!section?.jumpLinkInfo?.list?.length) return null;
  return section.jumpLinkInfo;
}

export function resolveHotspotFeedCover(feed: HotspotFeed): string | undefined {
  return (
    feed?.postShow?.cover ||
    feed?.cover ||
    feed?.postShow?.attachments?.find((item) => item?.type === "image")?.url ||
    feed?.attachments?.find((item) => item?.type === "image")?.url
  );
}

export function resolveHotspotFeedCreator(
  feed: HotspotFeed
): HotspotFeedUser | undefined {
  return (
    feed?.postShow?.creator ||
    feed?.creator ||
    feed?.postShow?.user ||
    feed?.user
  );
}

export function getHotspotFeedAspectRatio(feed: HotspotFeed): number {
  const cover = resolveHotspotFeedCover(feed);

  if (!cover) return 1;

  try {
    const url = new URL(cover);
    const imgInfo = url.searchParams.get("imginfo");

    if (!imgInfo) return 1;

    const [width, height] = imgInfo.split(",");
    const finalWidth = Number(width?.slice(1));
    const finalHeight = Number(height?.slice(1));

    if (!finalWidth || !finalHeight) return 1;

    return finalWidth / finalHeight;
  } catch {
    return 1;
  }
}

export function buildHotspotFeedCards(feeds: HotspotFeed[]): HotspotFeedCard[] {
  return (feeds || []).map<HotspotFeedCard>((feed) => ({
    id: feed.id,
    cover: resolveHotspotFeedCover(feed),
    summary: feed?.postShow?.summary || feed?.summary,
    ratio: getHotspotFeedAspectRatio(feed),
    creator: resolveHotspotFeedCreator(feed),
    likeCount: feed?.likeCount ?? feed?.postShow?.likeCount,
  }));
}
